import React, { useState, useEffect } from "react";
import { useHistory, useParams } from "react-router-dom";
import {
    useSnackbar,
    Breadcrumb,
    BreadcrumbItem,
    Grid,
    GridColumn,
    Spinner
} from "@livingdesign/react";
import {
    Paper,
    Table,
    TableHead,
    TableBody,
    TableCell,
    TableRow,
    TableContainer,
    TableSortLabel,
} from "@mui/material";
import { getComparator, getErrorMessage, getMatterData, sortData } from "../../../helpers/utils";
import { getCurrencyAmount, amountFormat } from "../../../helpers/commonUtils";
import AFADetailsStyles from "./AFADetails.styles";
import {
    AFA_FAMILY_AND_TYPE, AFA_FAMILY_LABEL, AFA_NOT_FOUND, AFA_TERMS_AMOUNT_DATA, AFA_TERMS_NUMERIC_DATA,
    AFA_TERMS_PERCENTAGE_DATA, AFA_TYPE_LABEL, APPLIES_TO_LABEL, COMMENTS, EFFECTIVE_DATE_LABEL, END_DATE, FIRMS_ASSIGNED, INVOICE_NUMBER_LABEL, LEAD, LEAD_ATTORNEY,
    MATTER_FIELD,
    MATTER_STATUS, METADATA, NO, ORGANIZATION, PRACTICE_AREA, YES
} from "../../../constants/constants";
import { getService } from "../../../axios/axios";
import { ORGANIZATION_SERVICE } from "../../../constants/baseURLs";
import { displayLocalDate } from "../../../helpers/utcUtils";
import { useSelector } from "react-redux";

const AFADetails = () => {
    const classes = AFADetailsStyles();
    const history = useHistory();
    const { afaId } = useParams();
    const { addSnack } = useSnackbar();

    // State management
    const [afaData, setAfaData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [metaDataLoading, setMetaDataLoading] = useState(false);
    const [orderBy, setOrderBy] = useState('');
    const [order, setOrder] = useState('asc');
    const { companyName, identifier } = useSelector((state) => state?.organization?.organizationDetailValues);
    const [afaTerms, setAFATerms] = useState({});
    const [metadata, setMetaData] = useState({});
    const masterDetails = useSelector(state => state?.masterData?.masterDatas);

    const getAFATerms = (afaData) => {
        const afaTerms = AFA_FAMILY_AND_TYPE?.filter((afa) => {
            return afa?.family === afaData?.family && afa?.type === afaData?.type
        })
        setAFATerms(afaTerms[0])
    }

    // Load AFA data
    useEffect(() => {
        const fetchAFAData = async () => {
            setLoading(true);
            try {
                // Mock API call - replace with actual API
                getService(ORGANIZATION_SERVICE, `/organizations/afas/v1/${afaId}`)
                    .then((res) => {
                        const response = res?.data;
                        if (response?.appliedTo === MATTER_FIELD) {
                            fetchMatterDataForMetaData(response?.appliedToObjectId)
                        }
                        getAFATerms(response)
                        setAfaData(response);
                        setLoading(false);
                    }).catch((error) => {
                        setLoading(false)
                        addSnack({
                            message: getErrorMessage(error)
                        })
                    });
            } catch (error) {
                setLoading(false)
                addSnack({
                    message: getErrorMessage(error)
                })
            }
        };

        const fetchMatterDataForMetaData = (matterId) => {
            setMetaDataLoading(true);
            getMatterData(matterId, identifier)
                .then((res) => {
                    if (res?.data?.result?.matter) {
                        setMetaData(res?.data?.result?.matter)
                    } else {
                        getErrorMessage(res)
                    }
                })
                .catch((error) => {
                    getErrorMessage(error)
                })
                .finally(() => {
                    setMetaDataLoading(false);
                })
        }

        //Fetch AFA Data
        fetchAFAData();
    }, [afaId]);

    useEffect(() => {
		if (masterDetails && Object.keys(masterDetails).length && afaData?.terms?.length) {
			const expenseCode = masterDetails?.expenseCode?.masterData || [];
			const phaseCode = masterDetails?.phaseCode?.masterData || [];
			const taskCode = masterDetails?.taskCode?.masterData || [];
			const feeCode = masterDetails?.feeCode?.masterData || [];
			const utbmsEntries = [...expenseCode, ...phaseCode, ...taskCode, ...feeCode];

			const updatedTerms = afaData.terms.map(term => {
				if (term?.utbmsCode) {
					const utbmsEntry = utbmsEntries?.find(entry => entry?.code == term?.utbmsCode);
					return {
						...term,
						utbmsDescription: utbmsEntry?.description || utbmsEntry?.name || 'Description not found'
					};
				}
				return term;
			});

			// Update the afaData state with the enhanced terms
			setAfaData(prevData => ({
				...prevData,
				terms: updatedTerms
			}));
		}
	}, [masterDetails, afaData?.terms?.length]);

    const handleRequestSort = (property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    if (loading) {
        return (
            <div className={classes.container}>
                <div className={classes.loadingContainer}>Loading...</div>
            </div>
        );
    }

    if (!afaData) {
        return (
            <div className={classes.container}>
                <div className={classes.notFoundContainer}>{AFA_NOT_FOUND}</div>
            </div>
        );
    }

    const displayAFATermTableCell = (column, data) => {
        let displayData = data;
        if (AFA_TERMS_AMOUNT_DATA.includes(column?.code)) {
            displayData = getCurrencyAmount(amountFormat(data))
        } else if (AFA_TERMS_PERCENTAGE_DATA.includes(column?.code)) {
            displayData = `${data}%`
        } else if (column?.code === "isOutcomeAchieved") {
            displayData = data ? YES : NO
        }
        return displayData;
    }

    return (
        <div className={classes.container}>
            {/* Breadcrumbs */}
            <div style={{ marginBottom: '24px' }}>
                <Breadcrumb aria-label="breadcrumb">
                    <BreadcrumbItem
                        onClick={() => history.push(`/organization`)}
                        UNSAFE_style={{ cursor: "pointer", color: "#1976d2" }}
                    >
                        {companyName || "-"}
                    </BreadcrumbItem>
                    <BreadcrumbItem isCurrent>
                        {afaData?.name}
                    </BreadcrumbItem>
                </Breadcrumb>
            </div>

            {/* Header Section */}
            <div className={classes.headerContainer}>
                <div className={classes.titleSection}>
                    <h1 className={classes.title}>
                        {afaData?.name}
                        <span className={classes.subTitle}>Created on: {displayLocalDate(afaData?.createdTs)}</span>
                    </h1>
                    <span className={classes.statusChip}>{afaData?.status}</span>
                </div>
            </div>

            {/* Basic Information */}
            <Grid>
                <GridColumn sm={9} lg={9} md={9}>
                    <div className={classes.infoCard}>
                        <div className={classes.infoGrid}>
                            <div className={classes.infoItem}>
                                <div className={classes.infoLabel}>{ORGANIZATION}</div>
                                <div className={classes.infoValue}>{companyName || "-"}</div>
                            </div>
                            <div className={classes.infoItem}>
                                <div className={classes.infoLabel}>{AFA_FAMILY_LABEL}</div>
                                <div className={classes.infoValue}>{afaData?.family}</div>
                            </div>
                            <div className={classes.infoItem}>
                                <div className={classes.infoLabel}>{AFA_TYPE_LABEL}</div>
                                <div className={classes.infoValue}>{afaData?.type}</div>
                            </div>
                            <div className={classes.infoItem}>
                                <div className={classes.infoLabel}>{APPLIES_TO_LABEL}</div>
                                <div className={classes.infoValueCapitalize}>{afaData?.appliedTo?.toLowerCase()}</div>
                            </div>
                            <div className={classes.infoItem}>
                                <div className={classes.infoLabel}>{EFFECTIVE_DATE_LABEL}</div>
                                <div className={classes.infoValue}>{displayLocalDate(afaData?.startDate)}</div>
                            </div>
                            <div className={classes.infoItem}>
                                <div className={classes.infoLabel}>{END_DATE}</div>
                                <div className={classes.infoValue}>{displayLocalDate(afaData?.endDate)}</div>
                            </div>
                        </div>

                        {/* AFA Terms Table */}
                        {afaTerms?.showTable && <>
                            <div className={classes.sectionTitle}>{afaTerms?.afaTermsHeading}</div>
                            <TableContainer component={Paper} className={classes.phaseTable}>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            {afaTerms?.afaTermsColumns?.map((column) => {
                                                return <TableCell>
                                                    <TableSortLabel
                                                        active={orderBy === column?.code}
                                                        direction={orderBy === column?.code ? order : 'asc'}
                                                        onClick={() => handleRequestSort(column?.code)}
                                                    >
                                                        {column?.name}
                                                    </TableSortLabel>
                                                </TableCell>
                                            })}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {sortData(afaData?.terms || [], getComparator(AFA_TERMS_NUMERIC_DATA, order, orderBy))?.map((terms, index) => (
                                            <TableRow key={index}>
                                                {afaTerms?.afaTermsColumns?.map((column) => {
                                                    return <TableCell>{displayAFATermTableCell(column, terms?.[column?.code])}</TableCell>
                                                })}
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </>
                        }
                    </div>
                </GridColumn>
                <GridColumn sm={3} lg={3} md={3}>
                    {/* Metadata Section */}
                    <div className={classes.infoCardRight}>
                        <div className={classes.sectionTitle}>{METADATA} {metaDataLoading && <span className={classes.metaDataSpinner}><Spinner color="gray" size="small" UNSAFE_className={classes.spinnerIcon} /></span>}</div>
                        <div>
                            {afaData?.appliedTo === MATTER_FIELD && <>
                                <div className={classes.metadataItem}>
                                    <span className={classes.metadataLabel}>{PRACTICE_AREA}</span>
                                    <span className={classes.metadataValue}>{metadata?.practiceArea || "-"}</span>
                                </div>
                                <div className={classes.metadataItem}>
                                    <span className={classes.metadataLabel}>{MATTER_STATUS}</span>
                                    <span className={classes.metadataValue}>{metadata?.status || "-"}</span>
                                </div>
                                <div className={classes.metadataItem}>
                                    <span className={classes.metadataLabel}>{LEAD_ATTORNEY}</span>
                                    <span className={classes.metadataValue}>{metadata?.leadAttorney || "-"}</span>
                                </div>
                                <div className={classes.metadataItem}>
                                    <span className={classes.metadataLabel}>{FIRMS_ASSIGNED}</span>
                                    <span className={classes.metadataValue}>{metadata?.firmsAssigned || "-"}</span>
                                </div>
                                <div className={classes.metadataItem}>
                                    <span className={classes.metadataLabel}>{INVOICE_NUMBER_LABEL}</span>
                                    <span className={classes.metadataValue}>{metadata?.invoices || "-"}</span>
                                </div>
                            </>}
                            <div className={classes.metadataItem}>
                                <span className={classes.metadataLabel}>{LEAD}</span>
                                <span className={classes.metadataValue}>{afaData?.ownerUserId || "-"}</span>
                            </div>
                            <div className={classes.metadataItem}>
                                <span className={classes.metadataLabel}>{COMMENTS}</span>
                                <span className={classes.metadataValue}>{afaData?.comment || "-"}</span>
                            </div>
                        </div>
                    </div>
                </GridColumn>
            </Grid>
        </div>
    );
};

export default AFADetails;
